import { inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppSelectors } from './store/app.selectors';
import { AuthService } from './shared/core/auth.service';
import { filter, take } from 'rxjs';
import { Permission } from './administration/components/roles/role.model';

export interface NavPermissionItem {
  permission?: string | null;
  anyPermissions?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);

  public readonly permissions = signal<string[]>([]);

  public getPermissions(): void {
    this.store
      .select(AppSelectors.permissions)
      .pipe(
        filter((permissions): permissions is Permission[] => !!permissions?.length),
        take(1)
      )
      .subscribe((permissions) => {
        this.syncPermissions(permissions);
      });
  }

  public syncPermissions(allPermissions: Permission[]): void {
    const user = this.authService.getCurrentUser();
    const userPermissionIds = new Set(user?.permissions?.map((permission) => permission.permissionId) ?? []);

    const resolved = allPermissions
      .filter((permission) => userPermissionIds.has(permission.uuid))
      .map((permission) => `${permission.group.toLowerCase()}_${permission.action}`);

    this.permissions.set(resolved);
  }

  public hasPermission(action: string | null | undefined): boolean {
    if (!action) {
      return true;
    }

    return this.permissions().includes(action);
  }

  public hasAnyPermission(actions: Array<string | null | undefined>): boolean {
    return actions.some((action) => this.hasPermission(action));
  }

  public canShowNavItem(item: NavPermissionItem): boolean {
    if (item.anyPermissions?.length) {
      return this.hasAnyPermission(item.anyPermissions);
    }

    return this.hasPermission(item.permission ?? null);
  }
}
